import { Lock, LockOpen } from '@mui/icons-material';
import { Box, Button, FormControl, FormLabel, Stack, Switch, TextField, Typography } from '@mui/material';
import { unparse } from 'papaparse';
import { ContractSelection } from 'customer-management/customer-details/view-contracts/contract-selection';
import {
  ApproveDialog,
  CancelDialog,
  RejectDialog,
  RequestDialog,
} from 'customer-management/customer-details/view-contracts/dialogs';
import { useViewContractsState } from 'customer-management/customer-details/view-contracts/state/view-contracts-state';
import { useSnackbar } from 'notistack';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useParams } from 'react-router-dom';
import { ExportButton } from 'shared/components/export-button';
import {
  useCreateDraftContract,
  useSendContractNotification,
  useUpdateContract,
  useUpdateDraftContract,
} from 'shared/mutations';
import { useService } from 'shared/providers';
import {
  ContractDocument,
  ContractForm,
  ContractRouteParams,
  ExportContractTerms,
  GrowthTier,
  Term,
  UserInfo,
  VolumeTier,
} from 'shared/types';
import {
  browserDownloadCsv,
  contractDocumentToContractForm,
  contractFormToContractDocument,
  formatDate,
  formatMoney,
  formatPercent,
} from 'shared/utility';
import { ViewContractsGrowthIncentiveTiersTableContainer } from './tables/view-contracts-growth-incentive-tiers-container';
import { ViewContractsVolumeIncentiveTiersTableContainer } from './tables/view-contracts-volume-incentive-tiers-container';
import { ViewContractsContractTermsContainer } from './view-contracts-contract-terms-container';
import { ContractAgainstSelection } from './contract-selection-against';
import { useGetContract } from 'shared/queries';
import { useUserPermissions } from 'shared/hooks';
import { FormDatepickerField } from 'shared/controls';

interface ViewContractsProps {
  contract: ContractDocument;
  canReview: boolean;
  hasEditPermission: boolean;
  isDraft: boolean;
  user: UserInfo;
}

export function ViewContracts({ contract, canReview, isDraft, user, hasEditPermission }: ViewContractsProps) {
  const { enqueueSnackbar } = useSnackbar();
  const { appConfig } = useService();
  const environment = appConfig.environment;
  const state = useViewContractsState();
  const userPermissions = useUserPermissions();
  const hasControllerPermission = userPermissions.includes('abg-retail.controller');

  const { contractId } = useParams<ContractRouteParams>();
  const { contract: contractToDraft } = useGetContract(contractId);
  const diffingContract = useGetContract(state.diffingContract.get() ?? '-')?.contract;

  const editMode = state.editMode.get();
  const diffingMode = state.diffingMode.get();

  const defaultForm = useMemo(() => contractDocumentToContractForm(contract), [contract]);

  const methods = useForm({ defaultValues: defaultForm });

  const {
    handleSubmit,
    reset,
    watch,
    formState: { isDirty, errors },
    trigger,
  } = methods;

  const createDraftContractMutation = useCreateDraftContract();
  const updateDraftContractMutation = useUpdateDraftContract();
  const updateContractMutation = useUpdateContract();
  const notificationMutation = useSendContractNotification();

  const onSubmit = useCallback(
    async (form: ContractForm) => {
      const comments = state.comments.value;

      contract.approvalStatus = 'Pending Approval';
      await updateContractMutation.mutateAsync(contract);

      const today = new Date();
      const contractToUpdate = contractFormToContractDocument(form);
      contractToUpdate.modifiedBy = user.displayName;
      contractToUpdate.modifiedByEmail = user.email;
      contractToUpdate.modifiedDate = today.toISOString();
      contractToUpdate.markedForDeletionOrRestorationOn = '';
      contractToUpdate.startDate = form.startDate ? new Date(form.startDate).toISOString() : '';
      contractToUpdate.endDate = form.endDate ? new Date(form.endDate).toISOString() : '';

      // this shouldn't be needed but we store 0.00542 instead of 0.542 in the DB
      contractToUpdate.volumeTiers.forEach((volume: VolumeTier) => {
        volume.rebate /= 100;
      });
      contractToUpdate.growthTiers.forEach((grow: GrowthTier) => {
        grow.rebate /= 100;
      });

      contractToUpdate.status = 'pending';
      contractToUpdate.approvalStatus = 'Pending Approval';

      if (contract.status === 'rejected') {
        await updateDraftContractMutation.mutateAsync(contractToUpdate);
      } else {
        await createDraftContractMutation.mutateAsync(contractToUpdate);
      }

      await notificationMutation.mutateAsync({
        contract: contractToUpdate,
        template: 'request',
        comments,
        environment,
      });

      enqueueSnackbar('Approval requested!', { variant: 'success' });

      state.merge({ requestDialog: false, editMode: false, comments: '' });
    },
    [
      contract,
      user,
      createDraftContractMutation,
      updateDraftContractMutation,
      updateContractMutation,
      state,
      notificationMutation,
      enqueueSnackbar,
      environment,
    ]
  );

  const [reviewComments, setReviewComments] = useState(contract.reviewComments ?? '');

  const handleReviewCommentSubmit = useCallback(async () => {
    const comments = state.comments.value;
    contract.reviewComments = reviewComments;

    await updateContractMutation.mutateAsync(contract);

    if (contract.status === 'draft' || contract.status === 'pending' || contract.status === 'rejected') {
      await updateDraftContractMutation.mutateAsync(contract);
    }

    await notificationMutation.mutateAsync({
      contract,
      template: 'request',
      comments,
      environment,
    });

    enqueueSnackbar('Comments updated!', { variant: 'success' });
  }, [
    contract,
    reviewComments,
    updateDraftContractMutation,
    updateContractMutation,
    state,
    notificationMutation,
    enqueueSnackbar,
    environment,
  ]);

  const openRequestDialog = useCallback(async () => {
    const isFormValid = await trigger();
    state.requestDialog.set(isFormValid);
  }, [state.requestDialog, trigger]);

  const openApproveDialog = useCallback(() => {
    state.approveDialog.set(true);
  }, [state.approveDialog]);

  const openRejectDialog = useCallback(() => {
    state.rejectDialog.set(true);
  }, [state.rejectDialog]);

  const openCancelDialog = useCallback(() => {
    state.cancelDialog.set(true);
  }, [state.cancelDialog]);

  const resetForm = useCallback(() => {
    reset(defaultForm);
  }, [defaultForm, reset]);

  useEffect(() => {
    reset(defaultForm);
  }, [reset, defaultForm]);

  const toggleEditMode = useCallback(() => {
    state.editMode.set(prev => !prev);
  }, [state.editMode]);

  const toggleDiffingMode = useCallback(
    (boolean: boolean) => {
      state.diffingMode.set(boolean);
    },
    [state.diffingMode]
  );

  const getContractApprovedDate = useCallback(() => {
    try {
      return contract?.approvedDate ? formatDate(new Date(contract.approvedDate)) : '';
    } catch (error) {
      console.log('invalid date format: ', contract?.approvedDate);
      return '';
    }
  }, [contract]);

  const getContractStartDate = useCallback(() => {
    try {
      return contract?.startDate ? formatDate(new Date(contract.startDate)) : '';
    } catch (error) {
      console.log('invalid date format: ', contract?.startDate);
      return '';
    }
  }, [contract]);

  const getContractDeletedDate = useCallback(() => {
    try {
      return contract?.deletedAt ? formatDate(new Date(contract.deletedAt)) : '';
    } catch (error) {
      console.log('invalid date format: ', contract?.deletedAt);
      return '';
    }
  }, [contract]);

  const exportContractTerms = useCallback(() => {
    const fileName = `ContractTerms-${contract.brand}.csv`;
    const fields = ['Customer name', 'Brand', 'Contract', 'Approval Date', 'Approved By', 'Effective Date'];

    const dataToExport: ExportContractTerms = {
      customerName: contract.customerName,
      brand: contract.brand,
      originalDocumentName: contract.originalDocumentName,
      approvedDate: getContractApprovedDate(),
      approvedBy: contract.approvedBy,
      startDate: getContractStartDate(),
    };

    contract.terms.forEach((term: Term) => {
      fields.push(term.display);
      const key = term.name.replace('-', '');
      dataToExport[key as keyof ExportContractTerms] =
        term.type === 'percent'
          ? formatPercent(term.amount)
          : term.type === 'dollar'
          ? formatMoney(term.amount)
          : `${term.amount}`;
    });

    contract.volumeTiers.forEach((tier: VolumeTier) => {
      fields.push(`Volume Incentive Revenue Tier  ${tier.tier}`);
      fields.push(`Volume Incentive Discount Tier ${tier.tier}`);
      const keyRevenue = `volumeTierRevenue${tier.tier}`;
      const keyDiscount = `volumeTierDiscount${tier.tier}`;
      dataToExport[keyRevenue as keyof ExportContractTerms] = formatMoney(tier.floor);
      dataToExport[keyDiscount as keyof ExportContractTerms] = formatPercent(tier.rebate);
    });

    contract.growthTiers.forEach((tier: GrowthTier) => {
      fields.push(`Growth Incentive Revenue Tier ${tier.tier}`);
      fields.push(`Growth Incentive Discount Tier ${tier.tier}`);
      const keyRevenue = `growthTierRevenue${tier.tier}`;
      const keyDiscount = `growthTierDiscount${tier.tier}`;
      dataToExport[keyRevenue as keyof ExportContractTerms] = formatMoney(tier.floor);
      dataToExport[keyDiscount as keyof ExportContractTerms] = formatPercent(tier.rebate);
    });

    const csvData = Object.values(dataToExport);
    const csv = unparse({ data: csvData, fields });
    browserDownloadCsv(csv, fileName);
  }, [contract, getContractApprovedDate, getContractStartDate]);

  const isPending = contract.status === 'pending';
  const isRejected = contract.status === 'rejected';

  const submitLoading =
    createDraftContractMutation.isLoading || updateDraftContractMutation.isLoading || notificationMutation.isLoading;

  const requestButton = (
    <Stack spacing={1}>
      <Box>
        <Button disabled={!isDirty} onClick={resetForm} variant='contained'>
          reset
        </Button>
      </Box>
      <Box>
        <Button disabled={!isDirty} onClick={openRequestDialog} variant='contained' color='secondary'>
          submit changes
        </Button>
      </Box>
    </Stack>
  );
  const shouldDeleteContract = contract.deletedAt;
  const shouldRestoreContract = contractToDraft?.deletedAt && !contract.deletedAt;

  const cancelButton = (
    <Box>
      <Button variant='contained' color='secondary' onClick={openCancelDialog}>
        {shouldDeleteContract
          ? 'cancel request for deletion'
          : shouldRestoreContract
          ? 'cancel request for restoration'
          : 'cancel request'}
      </Button>
    </Box>
  );

  return (
    <>
      <FormProvider {...methods}>
        <form id='contract-form' onSubmit={handleSubmit(onSubmit)}>
          <Box sx={{ display: 'grid', gap: 2 }}>
            <Box
              sx={{
                pt: 2,
                display: 'grid',
                gridTemplateColumns: '1fr .1fr 1fr',
                gap: 3,
              }}
            >
              <Box
                sx={{
                  display: 'grid',
                  alignContent: 'center',
                  gap: 2,
                  ...(!hasControllerPermission ? { gridTemplateColumns: 'repeat(2, 1fr)' } : ''),
                }}
              >
                <Typography variant='h5'>Current Contract Terms</Typography>
                <ContractSelection />
                {!hasControllerPermission && (
                  <>
                    {!isDraft && !editMode ? (
                      <>
                        <Typography variant='h5'>Compare Against</Typography>
                        <ContractAgainstSelection onChange={toggleDiffingMode} />
                      </>
                    ) : null}
                  </>
                )}
                {editMode && (
                  <>
                    <Box>
                      <FormDatepickerField
                        name='startDate'
                        label='Contract Start Date'
                        rules={{
                          required: {
                            message: 'Contract Start Date is required',
                            value: true,
                          },
                        }}
                        error={Boolean(errors.startDate)}
                        helperText={errors.startDate?.message}
                      />
                    </Box>
                    <Box>
                      <FormDatepickerField
                        name='endDate'
                        label='Contract End Date'
                        rules={{
                          required: {
                            message: 'Contract End Date is required',
                            value: true,
                          },
                          validate: {
                            isAfterDate: (value: string) => {
                              const startDate = watch('startDate') as string;
                              if (!startDate || !value) return undefined;

                              return new Date(startDate).getTime() > new Date(value).getTime()
                                ? 'Contract End Date should not be before Contract Start Date'
                                : undefined;
                            },
                          },
                        }}
                        error={Boolean(errors.endDate)}
                        helperText={errors.endDate?.message}
                      />
                    </Box>
                  </>
                )}
              </Box>
              <Box></Box>
              <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)' }}>
                <Stack spacing={1} sx={{ alignSelf: 'center', justifySelf: 'start' }}>
                  {isDraft ? (
                    canReview ? (
                      <>
                        {!isRejected && (
                          <Stack direction='row' spacing={1}>
                            <Box>
                              <Button color='secondary' variant='contained' onClick={openApproveDialog}>
                                {shouldDeleteContract
                                  ? 'approve deletion'
                                  : shouldDeleteContract
                                  ? 'approve restoration'
                                  : 'approve'}
                              </Button>
                            </Box>
                            <Box>
                              <Button
                                disabled={isRejected}
                                color='secondary'
                                variant='contained'
                                onClick={openRejectDialog}
                              >
                                {shouldDeleteContract
                                  ? 'reject deletion'
                                  : shouldDeleteContract
                                  ? 'reject restoration'
                                  : 'reject'}
                              </Button>
                            </Box>
                          </Stack>
                        )}
                        {!isRejected && cancelButton}
                        {editMode && requestButton}
                      </>
                    ) : (
                      <Stack direction='row' spacing={1}>
                        {!isRejected && cancelButton}
                        {editMode && requestButton}
                      </Stack>
                    )
                  ) : (
                    <>
                      {editMode ? (
                        requestButton
                      ) : (
                        <Stack spacing={0.5}>
                          <Typography variant='body2' sx={{ fontWeight: 'bold' }}>
                            Approval Date: {getContractApprovedDate()}
                          </Typography>
                          <Typography variant='body2' sx={{ fontWeight: 'bold' }}>
                            Effective Date: {getContractStartDate()}
                          </Typography>
                          <Typography variant='body2' sx={{ fontWeight: 'bold' }}>
                            Approved By: {contract?.approvedBy}
                          </Typography>
                          {contract?.deletedAt && (
                            <>
                              <Typography variant='body2' sx={{ fontWeight: 'bold' }}>
                                Deleted Date: {getContractDeletedDate()}
                              </Typography>
                              <Typography variant='body2' sx={{ fontWeight: 'bold' }}>
                                Deleted By: {contract?.deletedBy}
                              </Typography>
                            </>
                          )}
                        </Stack>
                      )}
                    </>
                  )}
                </Stack>
                <Box sx={{ justifySelf: 'center', alignSelf: 'center' }}>
                  <FormControl component='fieldset' variant='standard'>
                    <FormLabel component='legend'>Edit Terms</FormLabel>
                    <Stack direction='row'>
                      <Lock color={isPending ? 'error' : undefined} />
                      <Switch
                        checked={editMode}
                        disabled={isPending || !hasEditPermission || diffingMode}
                        onChange={toggleEditMode}
                        name='editTerms'
                        title={!hasEditPermission ? 'You do not have permission to perform this action' : ''}
                      />
                      <LockOpen color={editMode ? 'success' : undefined} />
                    </Stack>
                    {isPending && <Typography color='error.main'>Pending Approval</Typography>}
                    {isRejected && <Typography color='error.main'>Rejected</Typography>}
                  </FormControl>
                </Box>
                <Box sx={{ justifySelf: 'end', alignSelf: 'center' }}>
                  <ExportButton noun='Terms' onClick={exportContractTerms} />
                </Box>
              </Box>
            </Box>
            <Box sx={{ display: 'grid', gridTemplateColumns: '1fr .15fr 1fr' }}>
              <Box>
                <ViewContractsVolumeIncentiveTiersTableContainer
                  isEditMode={editMode}
                  isDiffingMode={diffingMode}
                  isDraft={isDraft}
                  isRejected={isRejected}
                  comparingContractTiers={isDraft ? contractToDraft?.volumeTiers : diffingContract?.volumeTiers}
                />
              </Box>
              <Box></Box>
              <Box>
                <ViewContractsGrowthIncentiveTiersTableContainer
                  isEditMode={editMode}
                  isDiffingMode={diffingMode}
                  isDraft={isDraft}
                  isRejected={isRejected}
                  comparingContractTiers={isDraft ? contractToDraft?.growthTiers : diffingContract?.growthTiers}
                />
              </Box>
            </Box>
            <Box>
              <ViewContractsContractTermsContainer
                isEditMode={editMode}
                isDiffingMode={diffingMode}
                isDraft={isDraft}
                isRejected={isRejected}
                comparingContractTerms={isDraft ? contractToDraft?.terms : diffingContract?.terms}
              />
            </Box>
          </Box>
        </form>
        <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
          <Box>
            <TextField
              label='Comments'
              disabled={!hasEditPermission}
              fullWidth
              multiline
              rows={5}
              inputProps={{
                maxLength: 500,
              }}
              value={reviewComments}
              onChange={event => {
                setReviewComments(event.target.value);
              }}
              helperText={`${500 - reviewComments.length} characters remaining`}
            />
          </Box>
          <Box sx={{ alignSelf: 'end' }}>
            <Button
              onClick={() => handleReviewCommentSubmit()}
              disabled={!hasEditPermission || reviewComments === contract.reviewComments}
              variant='contained'
              sx={{ marginBottom: 3 }}
            >
              Save Comments
            </Button>
          </Box>
        </Box>
        <RequestDialog loading={submitLoading} />
        <ApproveDialog contract={contract} user={user} environment={environment} />
        <RejectDialog contract={contract} environment={environment} />
        <CancelDialog contract={contract} environment={environment} />
      </FormProvider>
    </>
  );
}
